import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../../theme/useTheme';
import { useSelector } from 'react-redux';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  TextInput,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import FontFamily from '../../../../constants/fonts';
import { AdminColors } from '../../constants/adminColors';
// Local controlled toggle (visual copy of PTLanguageToggle)
export const ListLanguageToggle: React.FC<{ value: EquipmentLanguage; onChange: (l: EquipmentLanguage) => void; compact?: boolean }> = ({ value, onChange, compact = false }) => {
  return (
    <View style={toggleStyles.container}>
      {(['en','fr','es'] as EquipmentLanguage[]).map((lang, idx) => {
        const isActive = value === lang;
        return (
          <TouchableOpacity
            key={lang}
            style={[
              toggleStyles.btn,
              compact && toggleStyles.btnCompact,
              idx === 0 && toggleStyles.btnLeft,
              idx === 2 && toggleStyles.btnRight,
              isActive && toggleStyles.btnActive,
            ]}
            onPress={() => onChange(lang)}
            activeOpacity={0.75}
          >
            <Text style={[toggleStyles.label, isActive && toggleStyles.labelActive]}>{lang.toUpperCase()}</Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

const toggleStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    borderRadius: 8,
    overflow: 'hidden',
  },
  btn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: AdminColors.bgSurface,
  },
  btnCompact: {
    paddingHorizontal: 9,
    paddingVertical: 4,
  },
  btnLeft: {
    borderRightWidth: 1,
    borderRightColor: AdminColors.borderDefault,
  },
  btnRight: {},
  btnActive: {
    backgroundColor: AdminColors.blue700,
  },
  label: {
    fontFamily: FontFamily.bold,
    fontSize: 11,
    color: AdminColors.textSecondary,
    letterSpacing: 0.4,
  },
  labelActive: {
    color: AdminColors.textOnDark,
  },
});
import { AdminFormField, AdminTextInput } from '../../components/AdminFormField';
import AdminButton from '../../components/AdminButton';
import AdminModal from '../../components/AdminModal';
import AdminToggle from '../../components/AdminToggle';
import LHDataTable from '../../../../components/LHDataTable';
import { useToast } from '../../../../components/LhToastContainer';
import { EquipmentConfigItem, EquipmentLanguage, EquipmentPayload } from './types';
import { t } from './i18n';
import AdminSelect from '../../components/AdminSelect';
import AdminMultiSelect from '../../components/AdminMultiSelect';
import type { RootState } from '../../../../store';
import {
  fetchEquipment,
  createEquipment,
  updateEquipment,
  toggleEquipmentActive,
  checkEquipmentExists,
  checkEquipmentDisplayOrderExists,
  fetchRouteTypes,
} from './perfectTruckApi';



const ORG_TYPE_OPTIONS = [
  { label: 'PBNA', value: 'PBNA' },
  { label: 'FLNA', value: 'FLNA' },
  { label: '1NA', value: '1NA' },
];

const YES_NO_OPTIONS = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

const NO_YES_OPTIONS = [
  { label: 'No', value: 'no' },
  { label: 'Yes', value: 'yes' },
];

const ALL_ROUTE_TYPE_VALUE = '';

const normalizeRouteTypeToArray = (routeType: EquipmentConfigItem['routeType']): string[] => {
  const rawValues = Array.isArray(routeType)
    ? routeType.map(v => String(v).trim())
    : !routeType
      ? []
      : String(routeType)
          .split(',')
          .map(v => v.trim());

  // Treat both API shapes as "All": [] and ["All"].
  const hasAll = rawValues.some(v => v === '' || v.toLowerCase() === 'all');
  if (hasAll) {
    return [];
  }

  return rawValues.filter(Boolean);
};

const formatRouteType = (routeType: EquipmentConfigItem['routeType'], options: Array<{ label: string; value: string }> = []): string => {
  const values = normalizeRouteTypeToArray(routeType);
  if (values.length === 0) return 'All';
  // Route type is persisted as an ID; legacy entries stored the label directly.
  return values.map(v => options.find(o => o.value === v)?.label ?? v).join(', ');
};

const EMPTY_FORM: Omit<EquipmentConfigItem, 'id'> = {
  equipmentName: '',
  certifierPosLabel: 'Loaded',
  certifierNegLabel: 'Not Loaded',
  checkerPosLabel: 'Returned',
  checkerNegLabel: 'Not Returned',
  showOnCertifierForm: true,
  showOnCheckerForm: true,
  displayOrder: 0,
  isActive: true,
  isDuo: false,
  isFsv: false,
  routeType: [],
  locationType: null,
  organizationType: null,
  is3pl: false,
  isWhd: false,
  isTextBoxRequired: false,
};

const normalizeEquipmentItem = (raw: any): EquipmentConfigItem => {
  // Backend historically used several field names: `textFieldRequired`, `isTextFieldRequired`.
  // Normalize them to frontend `isTextBoxRequired` so the UI toggle binds reliably.
  const textFlag = (raw && (raw.isTextBoxRequired ?? raw.isTextFieldRequired ?? raw.textFieldRequired ?? raw.text_field_required)) as boolean | undefined;
  return {
    ...raw,
    isTextBoxRequired: !!textFlag,
  } as EquipmentConfigItem;
};

interface ErrorMap { [key: string]: string }

type EquipmentConfigProps = {
  listLanguage?: EquipmentLanguage;
  onListLanguageChange?: (l: EquipmentLanguage) => void;
};

const EquipmentConfig: React.FC<EquipmentConfigProps> = ({ listLanguage: externalListLanguage, onListLanguageChange }) => {
  const requestedBy = useSelector((state: RootState) =>
    state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? ''
  );
  const toast = useToast();
  const [items, setItems] = useState<EquipmentConfigItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState('');
  const [editItem, setEditItem] = useState<EquipmentConfigItem | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // common (non-translated) form state
  const [commonForm, setCommonForm] = useState<Omit<EquipmentConfigItem, 'id'>>(EMPTY_FORM);
  const [errors, setErrors] = useState<ErrorMap>({});
  // list language filter (EN by default). Can be controlled by parent via props.
  const [internalListLanguage, setInternalListLanguage] = useState<EquipmentLanguage>('en');
  const currentListLanguage = externalListLanguage ?? internalListLanguage;

  const handleListLanguageChange = (l: EquipmentLanguage) => {
    if (onListLanguageChange) onListLanguageChange(l);
    else setInternalListLanguage(l);
  };

  // modal language (affects which translation fields are shown). Separate states for translations.
  const [modalLanguage, setModalLanguage] = useState<EquipmentLanguage>('en');
  const [enForm, setEnForm] = useState({ equipmentName: '', certifierPosLabel: 'Loaded', certifierNegLabel: 'Not Loaded', checkerPosLabel: 'Returned', checkerNegLabel: 'Not Returned' });
  const [frForm, setFrForm] = useState({ equipmentName: '', certifierPosLabel: '', certifierNegLabel: '', checkerPosLabel: '', checkerNegLabel: '' });
  const [esForm, setEsForm] = useState({ equipmentName: '', certifierPosLabel: '', certifierNegLabel: '', checkerPosLabel: '', checkerNegLabel: '' });
  const [modalValidationError, setModalValidationError] = useState<string>('');
  const [modalLoading, setModalLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [routeTypeOptions, setRouteTypeOptions] = useState<Array<{ label: string; value: string }>>([]);

  const modalVisible = isAdding || editItem !== null;

  const loadItems = useCallback(async () => {
    try {
      console.debug('[EquipmentConfig] loadItems called, listLanguage=', currentListLanguage);
      setIsLoading(true);
      setApiError('');
      // Equipment data should always be loaded in English (data does not switch with UI language)
      const [equipmentData, routeTypesData] = await Promise.all([
        fetchEquipment(currentListLanguage),
        fetchRouteTypes(currentListLanguage),
      ]);
      const normalized = equipmentData.map(e => normalizeEquipmentItem(e));
      setItems(normalized);
      const routeOptions = [
        { label: 'All', value: '' },
        ...routeTypesData.map(rt => ({
          label: rt.routeType,
          value: String(rt.id),
        })),
      ];
      setRouteTypeOptions(routeOptions);
    } catch (e: any) {
      setApiError(e?.response?.data?.message ?? 'Failed to load equipment items.');
    } finally {
      setIsLoading(false);
    }
  }, [currentListLanguage]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const validate = (f: Omit<EquipmentConfigItem, 'id'>, currentId?: number): ErrorMap => {
    const errs: ErrorMap = {};
    // Validate display order only (translated fields validated separately)
    if (!f.displayOrder || f.displayOrder < 1) { errs.displayOrder = 'Display order must be a positive integer.'; }
    else {
      const dupOrder = items.find(i => i.displayOrder === f.displayOrder && i.id !== currentId);
      if (dupOrder) errs.displayOrder = `Display order ${f.displayOrder} is already in use.`;
    }
    // Name uniqueness check uses English translation
    const nameToCheck = enForm?.equipmentName?.trim() ?? '';
    if (!nameToCheck) { errs.name = 'Equipment name is required.'; }
    else if (nameToCheck.length > 50) { errs.name = 'Equipment name cannot exceed 50 characters.'; }
    else {
      const dup = items.find(i => i.equipmentName.toLowerCase() === nameToCheck.toLowerCase() && i.id !== currentId);
      if (dup) errs.name = `"${nameToCheck}" already exists.`;
    }
    return errs;
  };

  const handleToggleField = async (id: number, field: 'showOnCertifierForm' | 'showOnCheckerForm' | 'isDuo' | 'isFsv' | 'isActive' | 'is3pl' | 'isWhd') => {
    const item = items.find(i => i.id === id);
    if (!item) return;
    if (field === 'isActive') {
      try {
        const updated = await toggleEquipmentActive(id, !item.isActive, requestedBy);
        setItems(prev => prev.map(i => i.id === id ? updated : i));
        toast.showToast({ header: t(currentListLanguage, 'toast.updateSuccess'), variant: 'success' });
      } catch (e: any) {
        setApiError(e?.response?.data?.message ?? 'Failed to toggle active status.');
      }
    } else {
      // Non-active toggles: optimistic update + save via PUT
      const updatedCommon = { ...item, [field]: !item[field] } as Omit<EquipmentConfigItem, 'id'>;
      setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: !i[field] } : i));
      try {
        // fetch existing translations across languages
        const [enRes, frRes, esRes] = await Promise.all([
          fetchEquipment('en').catch(() => []),
          fetchEquipment('fr').catch(() => []),
          fetchEquipment('es').catch(() => []),
        ]);
        const findById = (arr: EquipmentConfigItem[]) => arr.find(r => r.id === id);
        const en = findById(enRes as EquipmentConfigItem[]) ;
        const fr = findById(frRes as EquipmentConfigItem[]) ;
        const es = findById(esRes as EquipmentConfigItem[]) ;
        const translations = {
          en: { equipmentName: en?.equipmentName ?? item.equipmentName, certifierPosLabel: en?.certifierPosLabel ?? item.certifierPosLabel, certifierNegLabel: en?.certifierNegLabel ?? item.certifierNegLabel, checkerPosLabel: en?.checkerPosLabel ?? item.checkerPosLabel, checkerNegLabel: en?.checkerNegLabel ?? item.checkerNegLabel },
          fr: { equipmentName: fr?.equipmentName ?? '', certifierPosLabel: fr?.certifierPosLabel ?? '', certifierNegLabel: fr?.certifierNegLabel ?? '', checkerPosLabel: fr?.checkerPosLabel ?? '', checkerNegLabel: fr?.checkerNegLabel ?? '' },
          es: { equipmentName: es?.equipmentName ?? '', certifierPosLabel: es?.certifierPosLabel ?? '', certifierNegLabel: es?.certifierNegLabel ?? '', checkerPosLabel: es?.checkerPosLabel ?? '', checkerNegLabel: es?.checkerNegLabel ?? '' },
        };
        const payloadArray = buildMultilingualEquipmentPayload(updatedCommon, translations as any);
        await updateEquipment(id, payloadArray);
      } catch (e: any) {
        // Revert on failure
        setItems(prev => prev.map(i => i.id === id ? item : i));
        setApiError(e?.response?.data?.message ?? 'Failed to save change.');
      }
    }
  };

  const openAdd = () => {
    setEditItem(null);
    setIsAdding(true);
    setCommonForm({ ...EMPTY_FORM, displayOrder: items.length + 1 });
    setEnForm({ equipmentName: '', certifierPosLabel: 'Loaded', certifierNegLabel: 'Not Loaded', checkerPosLabel: 'Returned', checkerNegLabel: 'Not Returned' });
    setFrForm({ equipmentName: '', certifierPosLabel: '', certifierNegLabel: '', checkerPosLabel: '', checkerNegLabel: '' });
    setEsForm({ equipmentName: '', certifierPosLabel: '', certifierNegLabel: '', checkerPosLabel: '', checkerNegLabel: '' });
    setModalLanguage('en');
    setErrors({});
    setApiError('');
    setHasUnsavedChanges(false);
  };

  const openEdit = (item: EquipmentConfigItem) => {
    setIsAdding(false);
    setEditItem(item);
    const { id: _id, ...rest } = item;
    // routeType is persisted as an ID; resolve legacy label-based entries to IDs so the
    // multi-select correctly reflects the current selection.
    const resolvedRouteTypes = normalizeRouteTypeToArray(item.routeType).map(raw => {
      const byId = routeTypeOptions.find(o => o.value === raw);
      if (byId) return byId.value;
      const byLabel = routeTypeOptions.find(o => o.label.trim().toLowerCase() === raw.trim().toLowerCase());
      return byLabel ? byLabel.value : raw;
    });
    setCommonForm({ ...rest, routeType: resolvedRouteTypes });
    // Load translations for this item across languages (EN/FR/ES)
    (async () => {
      try {
        setModalLoading(true);
        const [enRes, frRes, esRes] = await Promise.all([
          fetchEquipment('en').catch(() => []),
          fetchEquipment('fr').catch(() => []),
          fetchEquipment('es').catch(() => []),
        ]);
        const findById = (arr: EquipmentConfigItem[]) => arr.find(r => r.id === item.id);
        const en = findById(enRes as EquipmentConfigItem[]) ;
        const fr = findById(frRes as EquipmentConfigItem[]) ;
        const es = findById(esRes as EquipmentConfigItem[]) ;
        setEnForm({ equipmentName: en?.equipmentName ?? '', certifierPosLabel: en?.certifierPosLabel ?? 'Loaded', certifierNegLabel: en?.certifierNegLabel ?? 'Not Loaded', checkerPosLabel: en?.checkerPosLabel ?? 'Returned', checkerNegLabel: en?.checkerNegLabel ?? 'Not Returned' });
        setFrForm({ equipmentName: fr?.equipmentName ?? '', certifierPosLabel: fr?.certifierPosLabel ?? '', certifierNegLabel: fr?.certifierNegLabel ?? '', checkerPosLabel: fr?.checkerPosLabel ?? '', checkerNegLabel: fr?.checkerNegLabel ?? '' });
        setEsForm({ equipmentName: es?.equipmentName ?? '', certifierPosLabel: es?.certifierPosLabel ?? '', certifierNegLabel: es?.certifierNegLabel ?? '', checkerPosLabel: es?.checkerPosLabel ?? '', checkerNegLabel: es?.checkerNegLabel ?? '' });
      } catch (e) {
        // ignore — errors surfaced elsewhere
      } finally {
        setModalLoading(false);
      }
    })();
    setErrors({});
    setApiError('');
    setHasUnsavedChanges(false);
  };

  const doClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmClose(true);
    } else {
      setIsAdding(false);
      setEditItem(null);
      setErrors({});
      setHasUnsavedChanges(false);
    }
  };

  const confirmClose = () => {
    setIsAdding(false);
    setEditItem(null);
    setErrors({});
    setHasUnsavedChanges(false);
    setShowConfirmClose(false);
  };

  // Ensure modal state resets when fully closed
  useEffect(() => {
    if (!modalVisible) {
      setEnForm({ equipmentName: '', certifierPosLabel: 'Loaded', certifierNegLabel: 'Not Loaded', checkerPosLabel: 'Returned', checkerNegLabel: 'Not Returned' });
      setFrForm({ equipmentName: '', certifierPosLabel: '', certifierNegLabel: '', checkerPosLabel: '', checkerNegLabel: '' });
      setEsForm({ equipmentName: '', certifierPosLabel: '', certifierNegLabel: '', checkerPosLabel: '', checkerNegLabel: '' });
      setModalLanguage('en');
      setModalValidationError('');
    }
  }, [modalVisible]);

  const buildCommonPayload = (f: Omit<EquipmentConfigItem, 'id'>) => {
    const normalizedRouteTypes = normalizeRouteTypeToArray(f.routeType);

    return {
      showOnCertifierForm: f.showOnCertifierForm,
      showOnCheckerForm: f.showOnCheckerForm,
      displayOrder: f.displayOrder,
      isDuo: f.isDuo,
      isFsv: f.isFsv,
      routeType: normalizedRouteTypes.length > 0 ? normalizedRouteTypes : [],
      locationType: f.locationType ?? null,
      organizationType: f.organizationType ?? null,
      is3pl: f.is3pl,
      isTextBoxRequired: f.isTextBoxRequired ?? false,
      isWhd: f.isWhd ?? false,
      isActive: f.isActive ?? true,
      requestedBy,
    } as any;
  };

  const buildMultilingualEquipmentPayload = (common: Omit<EquipmentConfigItem, 'id'>, translations: { [k in EquipmentLanguage]: any }): EquipmentPayload[] => {
    const commonPayload = buildCommonPayload(common);
    const make = (lang: EquipmentLanguage, t: any): EquipmentPayload => ({
      equipmentName: (t.equipmentName ?? '').trim(),
      certifierPosLabel: t.certifierPosLabel ?? '',
      certifierNegLabel: t.certifierNegLabel ?? '',
      checkerPosLabel: t.checkerPosLabel ?? '',
      checkerNegLabel: t.checkerNegLabel ?? '',
      showOnCertifierForm: commonPayload.showOnCertifierForm,
      showOnCheckerForm: commonPayload.showOnCheckerForm,
      displayOrder: commonPayload.displayOrder,
      isDuo: commonPayload.isDuo,
      isFsv: commonPayload.isFsv,
      isWhd: commonPayload.isWhd,
      routeType: commonPayload.routeType,
      locationType: commonPayload.locationType,
      organizationType: commonPayload.organizationType,
      is3pl: commonPayload.is3pl,
      isActive: commonPayload.isActive,
      requestedBy: commonPayload.requestedBy,
      language: lang,
      isTextBoxRequired: commonPayload.isTextBoxRequired,
    });

    return [make('en', translations.en), make('fr', translations.fr), make('es', translations.es)];
  };

  const handleRouteTypesChange = (selectedValues: string[]) => {
    const hasAll = selectedValues.includes(ALL_ROUTE_TYPE_VALUE);

    if (hasAll) {
      setF('routeType', []);
      return;
    }

    const deduped = Array.from(new Set(selectedValues.filter(Boolean)));
    setF('routeType', deduped);
  };
  const validateTranslations = (translations: { [k in EquipmentLanguage]: any }) => {
    const missing: EquipmentLanguage[] = [];
    (['en','fr','es'] as EquipmentLanguage[]).forEach(lang => {
      const t = translations[lang];
      if (!t.equipmentName || !t.equipmentName.trim() || !t.certifierPosLabel || !t.certifierPosLabel.trim() || !t.certifierNegLabel || !t.certifierNegLabel.trim() || !t.checkerPosLabel || !t.checkerPosLabel.trim() || !t.checkerNegLabel || !t.checkerNegLabel.trim()) {
        missing.push(lang);
      }
    });
    return missing;
  };

  const handleSave = async () => {
    // validate common fields
    const errs = validate(commonForm, editItem?.id);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    // validate translations for all languages
    const translations = { en: enForm, fr: frForm, es: esForm };
    const missing = validateTranslations(translations);
    if (missing.length > 0) {
      setModalValidationError(t(modalLanguage, 'validation.completeTranslations'));
      return;
    }

    try {
      setIsSaving(true);
      const payloadArray = buildMultilingualEquipmentPayload(commonForm, translations);

      if (!editItem) {
        // checks: existence uses English equipmentName for uniqueness check
        const [nameExists, orderExists] = await Promise.all([
          checkEquipmentExists(enForm.equipmentName.trim()),
          checkEquipmentDisplayOrderExists(commonForm.displayOrder),
        ]);
        const checkErrs: ErrorMap = {};
        if (nameExists) checkErrs.name = 'Equipment Already Exists';
        if (orderExists) checkErrs.displayOrder = 'Display Order already exists';
        if (Object.keys(checkErrs).length > 0) {
          setErrors(checkErrs);
          setIsSaving(false);
          return;
        }
      }

        if (editItem) {
        const updatedRaw = await updateEquipment(editItem.id, payloadArray);
        const updated = normalizeEquipmentItem(updatedRaw);
        setItems(prev => prev.map(i => i.id === editItem.id ? updated : i));
        toast.showToast({ header: t(currentListLanguage, 'toast.updateSuccess'), variant: 'success' });
      } else {
        const createdRaw = await createEquipment(payloadArray);
        const created = normalizeEquipmentItem(createdRaw);
        setItems(prev => [...prev, created]);
        toast.showToast({ header: t(currentListLanguage, 'toast.addSuccess'), variant: 'success' });
      }
      confirmClose();
      // refresh list with current language filter
        await loadItems();
    } catch (e: any) {
      setApiError(e?.response?.data?.message ?? 'Failed to save equipment item.');
    } finally {
      setIsSaving(false);
    }
  };

  const setF = (key: keyof Omit<EquipmentConfigItem, 'id'>, val: any) => {
    setCommonForm(prev => ({ ...prev, [key]: val }));
    setHasUnsavedChanges(true);
  };

  const sorted = [...items].sort((a, b) => a.displayOrder - b.displayOrder);
  const filtered = searchQuery.trim()
    ? sorted.filter(i => i.equipmentName.toLowerCase().includes(searchQuery.trim().toLowerCase()))
    : sorted;

  return (
    <View>
      {/* Info banner */}
      <View style={styles.infoBanner}>
        <MaterialIcons name="check" size={16} color={AdminColors.blue700} style={styles.infoIcon} />
        <Text style={styles.infoText}>{t(currentListLanguage, 'infoBanner')}</Text>
      </View>

      {/* Toolbar: search + counts + clear + add */}
      <View style={styles.toolbar}>
        <View style={styles.toolbarLeft}>
          <View style={styles.searchBox}>
            <MaterialIcons name="search" size={16} color={AdminColors.textMuted} style={{ marginRight: 6 }} />
            <TextInput
              style={styles.searchInput}
              placeholder={t(currentListLanguage, 'searchPlaceholder')}
              placeholderTextColor={AdminColors.textMuted}
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')} activeOpacity={0.7}>
                <MaterialIcons name="close" size={14} color={AdminColors.textMuted} />
              </TouchableOpacity>
            )}
          </View>
          <Text style={styles.countText}>
            {`${items.filter(i => i.isActive).length} ${t(currentListLanguage, 'counts.active')}, ${items.filter(i => !i.isActive).length} ${t(currentListLanguage, 'counts.inactive')}`}
          </Text>
        </View>
        <View style={styles.toolbarRight}>
          {searchQuery.length > 0 && (
            <AdminButton
              label="Clear Filters"
              variant="secondary"
              small
              onPress={() => setSearchQuery('')}
            />
          )}
          <AdminButton
            label={t(currentListLanguage, 'addButton.equipment')}
            variant="primary"
            small
            onPress={openAdd}
          />
        </View>
      </View>

      {apiError ? (
        <View style={styles.errorBanner}>
          <MaterialIcons name="error-outline" size={16} color={AdminColors.red400} />
          <Text style={styles.errorText}>{apiError}</Text>
        </View>
      ) : null}

      {isLoading ? (
        <View style={styles.loadingWrapper}>
          <ActivityIndicator size="large" color={AdminColors.blue700} />
        </View>
      ) : (
        <View style={styles.surface}>
        <LHDataTable
          data={filtered}
          columns={[
            { columnKey: 'equipmentName', header: t(currentListLanguage, 'table.equipmentName'), flexGrow: 2, sortable: true },
            { columnKey: 'certifierPosLabel', header: t(currentListLanguage, 'table.certifier'), width: 110, sortable: false },
            { columnKey: 'certifierNegLabel', header: t(currentListLanguage, 'table.certifier'), width: 110, sortable: false },
            { columnKey: 'checkerPosLabel',   header: t(currentListLanguage, 'table.checker'), width: 110, sortable: false },
            { columnKey: 'checkerNegLabel',   header: t(currentListLanguage, 'table.checker'), width: 110, sortable: false },
            {
              columnKey: 'showOnCertifierForm',
              header: t(currentListLanguage, 'table.certifier'),
              width: 100,
              sortable: false,
              renderCell: (item: any) => (
                <Text style={styles.cellText}>{item.showOnCertifierForm ? t(currentListLanguage, 'label.yes') : t(currentListLanguage, 'label.no')}</Text>
              ),
            },
            {
              columnKey: 'showOnCheckerForm',
              header: t(currentListLanguage, 'table.checker'),
              width: 100,
              sortable: false,
              renderCell: (item: any) => (
                <Text style={styles.cellText}>{item.showOnCheckerForm ? t(currentListLanguage, 'label.yes') : t(currentListLanguage, 'label.no')}</Text>
              ),
            },
            { columnKey: 'displayOrder', header: t(currentListLanguage, 'table.order'), width: 110, sortable: true },
            {
              columnKey: 'isDuo',
              header: t(currentListLanguage, 'table.duo'),
              width: 70,
              sortable: false,
              renderCell: (item: any) => (
                <Text style={styles.cellText}>{item.isDuo ? t(currentListLanguage, 'label.yes') : t(currentListLanguage, 'label.no')}</Text>
              ),
            },
            {
              columnKey: 'isFsv',
              header: t(currentListLanguage, 'table.fsv'),
              width: 70,
              sortable: false,
              renderCell: (item: any) => (
                <Text style={styles.cellText}>{item.isFsv ? t(currentListLanguage, 'label.yes') : t(currentListLanguage, 'label.no')}</Text>
              ),
            },
            {
              columnKey: 'locationType',
              header: t(currentListLanguage, 'table.route'),
              width: 110,
              sortable: true,
              renderCell: (item: any) => (
                <Text style={styles.cellText}>{formatRouteType(item.routeType, routeTypeOptions)}</Text>
              ),
            },
            {
              columnKey: 'organizationType',
              header: t(currentListLanguage, 'table.org'),
              width: 90,
              sortable: true,
              renderCell: (item: any) => (
                <Text style={styles.cellText}>{item.organizationType || '—'}</Text>
              ),
            },
            {
              columnKey: 'is3pl',
              header: t(currentListLanguage, 'table.3pl'),
              width: 70,
              sortable: false,
              renderCell: (item: any) => (
                <Text style={styles.cellText}>{item.is3pl ? t(currentListLanguage, 'label.yes') : t(currentListLanguage, 'label.no')}</Text>
              ),
            },
            {
              columnKey: 'isWhd',
              header: t(currentListLanguage, 'table.whd'),
              width: 70,
              sortable: false,
              renderCell: (item: any) => (
                <Text style={styles.cellText}>{item.isWhd ? t(currentListLanguage, 'label.yes') : t(currentListLanguage, 'label.no')}</Text>
              ),
            },
            {
              columnKey: 'isActive',
              header: t(currentListLanguage, 'table.active'),
              width: 100,
              sortable: false,
              renderCell: (item: any) => (
                <TouchableOpacity onPress={() => handleToggleField(item.id, 'isActive')} activeOpacity={0.7}>
                  <View style={[styles.statusBadge, item.isActive ? styles.statusActive : styles.statusInactive]}>
                    <Text style={[styles.statusBadgeText, item.isActive ? styles.statusActiveText : styles.statusInactiveText]}>
                      {item.isActive ? t(currentListLanguage, 'counts.active') : t(currentListLanguage, 'counts.inactive')}
                    </Text>
                  </View>
                </TouchableOpacity>
              ),
            },
          ]}
          tableHeader=""
          tableDescription=""
          showHeaderTopBorder={false}
          onRowClick={(item) => openEdit(item as EquipmentConfigItem)}
          disableRowHighlight
          locale={{
            rowsPerPageLabel: t(currentListLanguage, 'pagination.rowsPerPage'),
            pageLabel: t(currentListLanguage, 'pagination.page'),
            pageOfLabel: t(currentListLanguage, 'pagination.of'),
            firstPageLabel: t(currentListLanguage, 'pagination.first'),
            prevPageLabel: t(currentListLanguage, 'pagination.previous'),
            nextPageLabel: t(currentListLanguage, 'pagination.next'),
            lastPageLabel: t(currentListLanguage, 'pagination.last'),
          }}
        />
      </View>
      )}

      {/* Add / Edit Modal */}
      <AdminModal
        visible={modalVisible}
        title={editItem ? t(modalLanguage, 'modal.editTitle') : t(modalLanguage, 'modal.addTitle')}
        onClose={doClose}
        width={760}
        footer={
          <>
            <AdminButton label={t(modalLanguage, 'button.cancel')} variant="secondary" onPress={doClose} />
            <AdminButton label={editItem ? t(modalLanguage, 'button.saveChanges') : t(modalLanguage, 'button.save')} variant="primary" onPress={handleSave} disabled={isSaving} />
          </>
        }
      >
        <ScrollView style={styles.modalScroll} showsVerticalScrollIndicator={false}>
          {/* Modal language toggle + validation error */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <View style={{ flex: 1, alignItems: 'flex-start' }}>
              {modalValidationError ? (
                <View style={[styles.errorBanner]}> 
                  <MaterialIcons name="error-outline" size={16} color={AdminColors.red400} />
                  <Text style={styles.errorText}>{modalValidationError}</Text>
                </View>
              ) : null}
            </View>
            <View style={{ marginLeft: 12 }}>
              <ListLanguageToggle value={modalLanguage} onChange={(l) => { setModalLanguage(l); setModalValidationError(''); }} compact />
            </View>
          </View>

          {/* Equipment Name — full width (translation-specific) */}
          <AdminFormField label={t(modalLanguage, 'form.equipmentName')} required error={errors.name}>
            <AdminTextInput
              value={(modalLanguage === 'en' ? enForm.equipmentName : modalLanguage === 'fr' ? frForm.equipmentName : esForm.equipmentName)}
              onChangeText={v => {
                if (modalLanguage === 'en') setEnForm(prev => ({ ...prev, equipmentName: v }));
                else if (modalLanguage === 'fr') setFrForm(prev => ({ ...prev, equipmentName: v }));
                else setEsForm(prev => ({ ...prev, equipmentName: v }));
                setErrors({}); setHasUnsavedChanges(true); setModalValidationError('');
              }}
              placeholder={t(modalLanguage, 'placeholder.example')}
              hasError={!!errors.name}
            />
          </AdminFormField>

          {/* Certifier labels (translation-specific) */}
          <View style={styles.formRow}>
            <View style={styles.formCol}>
              <AdminFormField label={t(modalLanguage, 'form.certifierPosLabel')} required>
                <AdminTextInput value={(modalLanguage === 'en' ? enForm.certifierPosLabel : modalLanguage === 'fr' ? frForm.certifierPosLabel : esForm.certifierPosLabel)} onChangeText={v => {
                  if (modalLanguage === 'en') setEnForm(prev => ({ ...prev, certifierPosLabel: v }));
                  else if (modalLanguage === 'fr') setFrForm(prev => ({ ...prev, certifierPosLabel: v }));
                  else setEsForm(prev => ({ ...prev, certifierPosLabel: v }));
                  setErrors({}); setModalValidationError('');
                }} />
              </AdminFormField>
            </View>
            <View style={styles.formCol}>
              <AdminFormField label={t(modalLanguage, 'form.certifierNegLabel')} required>
                <AdminTextInput value={(modalLanguage === 'en' ? enForm.certifierNegLabel : modalLanguage === 'fr' ? frForm.certifierNegLabel : esForm.certifierNegLabel)} onChangeText={v => {
                  if (modalLanguage === 'en') setEnForm(prev => ({ ...prev, certifierNegLabel: v }));
                  else if (modalLanguage === 'fr') setFrForm(prev => ({ ...prev, certifierNegLabel: v }));
                  else setEsForm(prev => ({ ...prev, certifierNegLabel: v }));
                  setErrors({}); setModalValidationError('');
                }} />
              </AdminFormField>
            </View>
          </View>

          {/* Checker labels (translation-specific) */}
          <View style={styles.formRow}>
            <View style={styles.formCol}>
              <AdminFormField label={t(modalLanguage, 'form.checkerPosLabel')} required>
                <AdminTextInput value={(modalLanguage === 'en' ? enForm.checkerPosLabel : modalLanguage === 'fr' ? frForm.checkerPosLabel : esForm.checkerPosLabel)} onChangeText={v => {
                  if (modalLanguage === 'en') setEnForm(prev => ({ ...prev, checkerPosLabel: v }));
                  else if (modalLanguage === 'fr') setFrForm(prev => ({ ...prev, checkerPosLabel: v }));
                  else setEsForm(prev => ({ ...prev, checkerPosLabel: v }));
                  setErrors({}); setModalValidationError('');
                }} />
              </AdminFormField>
            </View>
            <View style={styles.formCol}>
              <AdminFormField label={t(modalLanguage, 'form.checkerNegLabel')} required>
                <AdminTextInput value={(modalLanguage === 'en' ? enForm.checkerNegLabel : modalLanguage === 'fr' ? frForm.checkerNegLabel : esForm.checkerNegLabel)} onChangeText={v => {
                  if (modalLanguage === 'en') setEnForm(prev => ({ ...prev, checkerNegLabel: v }));
                  else if (modalLanguage === 'fr') setFrForm(prev => ({ ...prev, checkerNegLabel: v }));
                  else setEsForm(prev => ({ ...prev, checkerNegLabel: v }));
                  setErrors({}); setModalValidationError('');
                }} />
              </AdminFormField>
            </View>
          </View>

          {/* Show on forms */}
          <View style={styles.formRow}>
            <View style={styles.formCol}>
              <AdminFormField label={t(modalLanguage, 'form.showOnCertifier')}>
                <AdminSelect
                  value={commonForm.showOnCertifierForm ? 'yes' : 'no'}
                  onChange={v => setF('showOnCertifierForm', v === 'yes')}
                  options={YES_NO_OPTIONS}
                />
              </AdminFormField>
            </View>
            <View style={styles.formCol}>
              <AdminFormField label={t(modalLanguage, 'form.showOnChecker')}>
                <AdminSelect
                  value={commonForm.showOnCheckerForm ? 'yes' : 'no'}
                  onChange={v => setF('showOnCheckerForm', v === 'yes')}
                  options={YES_NO_OPTIONS}
                />
              </AdminFormField>
            </View>
          </View>

          {/* Text Box Requirement moved to toggles section below */}

          {/* Display Order / DUO */}
          <View style={styles.formRow}>
            <View style={styles.formCol}>
              <AdminFormField label={t(modalLanguage, 'form.displayOrder')} required error={errors.displayOrder}>
                <AdminTextInput
                  value={commonForm.displayOrder ? String(commonForm.displayOrder) : ''}
                  onChangeText={v => { setF('displayOrder', parseInt(v) || 0); setErrors({}); }}
                  keyboardType="number-pad"
                  placeholder={t(modalLanguage, 'placeholder.example')}
                  hasError={!!errors.displayOrder}
                />
              </AdminFormField>
            </View>
            <View style={styles.formCol}>
              <AdminFormField label={t(modalLanguage, 'form.duo')} required>
                <AdminSelect
                  value={commonForm.isDuo ? 'yes' : 'no'}
                  onChange={v => setF('isDuo', v === 'yes')}
                  options={NO_YES_OPTIONS}
                />
              </AdminFormField>
            </View>
          </View>

          {/* FSV / Route Type */}
          <View style={styles.formRow}>
            <View style={styles.formCol}>
              <AdminFormField label={t(modalLanguage, 'form.fsv')}>
                <AdminSelect
                  value={commonForm.isFsv ? 'yes' : 'no'}
                  onChange={v => setF('isFsv', v === 'yes')}
                  options={NO_YES_OPTIONS}
                />
              </AdminFormField>
            </View>
            <View style={styles.formCol}>
              <AdminFormField label={t(modalLanguage, 'form.routeType')}>
                <AdminMultiSelect
                  values={normalizeRouteTypeToArray(commonForm.routeType).length > 0
                    ? normalizeRouteTypeToArray(commonForm.routeType)
                    : [ALL_ROUTE_TYPE_VALUE]}
                  onChange={handleRouteTypesChange}
                  options={routeTypeOptions}
                  allValue={ALL_ROUTE_TYPE_VALUE}
                />
              </AdminFormField>
            </View>
          </View>

          {/* Org Type / 3PL */}
          <View style={styles.formRow}>
            <View style={styles.formCol}>
              <AdminFormField label={t(modalLanguage, 'form.orgType')}>
                <AdminSelect
                  value={commonForm.organizationType ?? ''}
                  onChange={v => setF('organizationType', v || null)}
                  options={ORG_TYPE_OPTIONS}
                  placeholder={t(modalLanguage, 'placeholder.selectOrg')}
                />
              </AdminFormField>
            </View>
            <View style={styles.formCol}>
              <AdminFormField label={t(modalLanguage, 'form.3plStatus')}>
                <AdminSelect
                  value={commonForm.is3pl ? 'yes' : 'no'}
                  onChange={v => setF('is3pl', v === 'yes')}
                  options={NO_YES_OPTIONS}
                />
              </AdminFormField>
            </View>
          </View>

          {/* WHD / Active */}
          <View style={styles.formRow}>
            <View style={styles.formCol}>
              <AdminFormField label={t(modalLanguage, 'form.whdStatus')}>
                <AdminSelect
                  value={commonForm.isWhd ? 'yes' : 'no'}
                  onChange={v => setF('isWhd', v === 'yes')}
                  options={NO_YES_OPTIONS}
                />
              </AdminFormField>
            </View>
            <View style={styles.formCol}>
              <AdminFormField label={t(modalLanguage, 'form.active')}>
                <AdminSelect
                  value={commonForm.isActive ? 'yes' : 'no'}
                  onChange={v => setF('isActive', v === 'yes')}
                  options={YES_NO_OPTIONS}
                />
              </AdminFormField>
            </View>
          </View>

          {/* Optional additional fields (toggle on left, label on right) */}
            <View style={{ marginTop: 12, marginBottom: 6 }}>
            <View style={styles.toggleRow}>
              <AdminToggle value={!!commonForm.isTextBoxRequired} onChange={v => setF('isTextBoxRequired', v)} />
              <View style={{ marginLeft: 10 }}>
                <Text style={styles.toggleLabel}>{t(modalLanguage, 'form.textField')}</Text>
                <Text style={styles.optionalHint}>{t(modalLanguage, 'form.textFieldHint')}</Text>
              </View>
            </View>
          </View>
        </ScrollView>
      </AdminModal>

      {/* Unsaved Changes Confirmation Modal */}
      <AdminModal
        visible={showConfirmClose}
        title={t(currentListLanguage, 'modal.unsavedTitle')}
        onClose={() => setShowConfirmClose(false)}
        width={500}
        footer={
          <>
            <AdminButton label={t(currentListLanguage, 'modal.unsaved.continue')} variant="secondary" onPress={() => setShowConfirmClose(false)} />
            <AdminButton label={t(currentListLanguage, 'modal.unsaved.close')} variant="primary" onPress={confirmClose} />
          </>
        }
      >
        <Text style={styles.confirmText}>{t(currentListLanguage, 'modal.unsaved.message')}</Text>
      </AdminModal>
    </View>
  );
};

const styles = StyleSheet.create({
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: AdminColors.blue50,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.blue200,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginTop: 12,
    marginBottom: 4,
  },
  infoIcon: {
    marginRight: 8,
    marginTop: 1,
  },
  infoText: {
    flex: 1,
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textPrimary,
    lineHeight: 20,
  },
  addButtonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 16,
    marginBottom: 4,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 16,
    marginBottom: 4,
    gap: 12,
    flexWrap: 'wrap',
  },
  toolbarLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 240,
  },
  toolbarRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 34,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    backgroundColor: AdminColors.bgSurface,
    minWidth: 200,
    flex: 1,
    maxWidth: 300,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    outlineWidth: 0,
  } as any,
  countText: {
    fontSize: 13,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
    whiteSpace: 'nowrap',
  } as any,
  loadingWrapper: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: AdminColors.red75,
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  errorText: {
    fontFamily: FontFamily.medium,
    fontSize: 13,
    color: AdminColors.red400,
    flex: 1,
  },
  surface: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    ...Platform.select({
      web: { boxShadow: '0 0 24px rgba(0,94,192,0.08)' } as any,
      default: { shadowColor: '#005EC0', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.08, shadowRadius: 24, elevation: 2 },
    }),
    overflow: 'hidden',
    marginTop: 12,
  },
  cellText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textPrimary,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 12,
    alignSelf: 'center',
  },
  statusActive: {
    backgroundColor: AdminColors.green75,
  },
  statusInactive: {
    backgroundColor: AdminColors.gray100 ?? '#EBEBEB',
  },
  statusBadgeText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
  },
  statusActiveText: {
    color: AdminColors.green600,
  },
  statusInactiveText: {
    color: AdminColors.textMuted ?? '#737373',
  },
  modalScroll: {
    maxHeight: 480,
  },
  formRow: {
    flexDirection: 'row',
    gap: 16,
  },
  formCol: {
    flex: 1,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  toggleLabel: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: AdminColors.textPrimary,
  },
  optionalHeader: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: AdminColors.textPrimary,
    opacity: 0.85,
  },
  optionalHint: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: AdminColors.textMuted,
    marginTop: 2,
  },
  confirmText: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: AdminColors.textPrimary,
    marginBottom: 8,
  },
});

export default EquipmentConfig;
